import { useQuery } from "@tanstack/react-query";
import ScheduleAttendanceCalendarService from "../../../../services/dispatch/schedule-attendance-calendar-service.js";
import React, {useMemo, useState} from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import FullCalendar from "@fullcalendar/react";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import "../utils/schedule-attendance-calendar-style.css";

function toYmd(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

function addMonths(date, months) {
    const d = new Date(date);
    d.setMonth(d.getMonth() + months);
    return d;
};

function capitalizeMonthName(text = "") {
    const months = [
        "janeiro", "fevereiro", "março", "abril", "maio", "junho",
        "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
    ];

    let formatted = text;

    for (const month of months) {
        const capitalized = month.charAt(0).toUpperCase() + month.slice(1);
        formatted = formatted.replace(new RegExp(`\\b${month}\\b`, "g"), capitalized);
    }

    return formatted;
}

function formatCalendarTitle(view) {
    const start = view.currentStart;
    const endExclusive = view.currentEnd;
    const endInclusive = new Date(endExclusive);
    endInclusive.setDate(endInclusive.getDate() - 1);

    const monthFormatter = new Intl.DateTimeFormat("pt-BR", {
        month: "long",
        year: "numeric",
    });

    const fullDateFormatter = new Intl.DateTimeFormat("pt-BR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    const dayFormatter = new Intl.DateTimeFormat("pt-BR", {
        day: "numeric",
    });

    if (view.type === "dayGridMonth") {
        return capitalizeMonthName(monthFormatter.format(start));
    }

    if (view.type === "timeGridDay") {
        return capitalizeMonthName(fullDateFormatter.format(start));
    }

    if (view.type === "timeGridWeek" || view.type === "listWeek") {
        const sameMonth =
            start.getMonth() === endInclusive.getMonth() &&
            start.getFullYear() === endInclusive.getFullYear();

        const sameYear = start.getFullYear() === endInclusive.getFullYear();

        if (sameMonth) {
            const startDay = dayFormatter.format(start);
            const endDay = dayFormatter.format(endInclusive);
            const monthYear = capitalizeMonthName(monthFormatter.format(start));
            return `${startDay} – ${endDay} de ${monthYear}`;
        }

        if (sameYear) {
            const startText = new Intl.DateTimeFormat("pt-BR", {
                day: "numeric",
                month: "long",
            }).format(start);

            const endText = new Intl.DateTimeFormat("pt-BR", {
                day: "numeric",
                month: "long",
                year: "numeric",
            }).format(endInclusive);

            return `${capitalizeMonthName(startText)} – ${capitalizeMonthName(endText)}`;
        }

        const startText = fullDateFormatter.format(start);
        const endText = fullDateFormatter.format(endInclusive);
        return `${capitalizeMonthName(startText)} – ${capitalizeMonthName(endText)}`;
    }

    return capitalizeMonthName(view.title ?? "");
};

export default function CalendarComponent({ openModal }){
    const [calendarTitle, setCalendarTitle] = useState("");

    const { data: events = [], isLoading, isError} = useQuery({
        queryKey: ["schedule-attendance-calendar"],
        queryFn: ScheduleAttendanceCalendarService.calendar,
    });

    const validRange = useMemo(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(),now.getMonth(), 1);
        const end = new Date(addMonths(start, 3).getFullYear(), addMonths(start, 3).getMonth(), 1);
        return { start: toYmd(start), end: toYmd(end) };
    }, []);

    const handleDatesSet = (info) => {
        setCalendarTitle(formatCalendarTitle(info.view));
    };

    const handleClick = (clickInfo) => {
        clickInfo.jsEvent.preventDefault();

        const popover = document.querySelector(".calendar-schedule .fc-popover");
        if (popover) {
            const closeBtn = popover.querySelector(".fc-popover-close");
            closeBtn?.click();
        };

        const event = clickInfo.event;
        const extendedProps = event.extendedProps || {};
        const startDate = event.start ? new Date(event.start) : null;

        const titleModal = startDate
            ? `${startDate.toLocaleDateString()} - ${startDate.toLocaleTimeString([], { timeStyle: "short" })}`
            : "";
        
        const descriptionLines = String(extendedProps?.description ?? "").split("\n");

        const titleNode = (
            <div className="p-1 text-zinc-600">
                <div className="text-xl font-semibold">
                    {event.title}
                    <span 
                        className="ml-3 inline-flex items-center text-sm cursor-pointer" 
                        onClick={(e) => {
                            e.stopPropagation();
                            window.open(ScheduleAttendanceCalendarService.event(event.id));
                        }}
                    >
                        <FaExternalLinkAlt />
                    </span>
                </div>
                {titleModal && <div className="mt-1 text-xs">{titleModal}</div>}
            </div>
        );

        const bodyNode = (
            <div className="p-1">
                <div className="space-y-5 text-sm text-zinc-600">
                    {extendedProps?.reason && (
                        <div>
                            <div className="font-semibold">Natureza</div>
                            <div>{extendedProps.reason}</div>
                        </div>
                    )}
                    {extendedProps?.place && (
                        <div>
                            <div className="font-semibold">Endereço</div>
                            <div>{extendedProps.place}</div>
                        </div>
                    )}
                    <div>
                        <div className="font-semibold">Descrição</div>
                        <div>
                            {descriptionLines.map((line, i) => (
                                <React.Fragment key={i}>
                                    {line}
                                    {i !== descriptionLines.length -1 && <br />}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );

        openModal({ title:titleNode, body: bodyNode });
    };

    return (
        <div className="calendar-schedule p-2 h-full min-h-0">
            <div className="calendar-toolbar-title-overlay">
                {calendarTitle}
            </div>

            <FullCalendar
                plugins={[interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin]}
                locale={ptBrLocale}
                headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
                }}
                height="100%"
                navLinks
                editable={false}
                dayMaxEvents
                events={events}
                validRange={validRange}
                eventClick={handleClick}
                datesSet={handleDatesSet}
            />
        </div>
    )
};